import React, { forwardRef, Ref, useImperativeHandle, useState } from 'react';
import { log } from 'src/js/service/logger';

export interface FormAggregatorClientInterface {
  prepareChanges: () => Promise<any>;
  applyChanges: () => Promise<any>;
  register: (id: string, prepareFunc: (() => Promise<any>) | null, applyFunc?: (() => Promise<any>) | null) => void;
  unregister: (id: string) => void;
  invalid: (id: string) => void;
  valid: (id: string) => void;
  debug: () => void;
}

interface MultipleFormAggregatorProps {
  className?: string;
}

export interface MultipleFormAggregatorRef extends FormAggregatorClientInterface {
  areAllClientsValid: () => boolean;
}

interface FormClientDef {
  prepareFunc?: () => Promise<any>;
  applyFunc?: () => Promise<any>;
  valid: boolean;
}

//let count = 0;
export const MultipleFormAggregator = forwardRef(
  (props: MultipleFormAggregatorProps, ref: Ref<MultipleFormAggregatorRef>) => {
    const { className, ...rest } = props;
    const [clientsMap] = useState<Map<string, FormClientDef>>(new Map());
    //const clientsMap = new Map(); using this line instead of the previous will break the unit test

    //console.log('MultipleFormAggregatorRef render', clientsMap.size, ++count);

    const reprocess = () => {
      let isValid = true;
      clientsMap.forEach((client, _key) => {
        if (client.valid === false) {
          isValid = false;
        }
      });
      //console.log('isValid?', isValid);
      return isValid;
    };

    const debug = () => {
      clientsMap.forEach((client, key) => {
        /* eslint-disable no-console */
        console.log('client', key, client);
        /* eslint-enable no-console */
      });
    };

    const areAllClientsValid = () => {
      const isValid = reprocess();
      //console.log('areAllClientsValid', isValid);
      return isValid;
    };

    const register = (id: string, prepareFunc?: () => Promise<any>, applyFunc?: () => Promise<any>) => {
      if (clientsMap.has(id)) {
        log.warn('client already exists, check your code');
        return; // already existing
      }
      clientsMap.set(id, { prepareFunc, applyFunc, valid: true });
      // dont need to reprocess, all are initially valid
    };

    const unregister = (id: string) => {
      if (!clientsMap.has(id)) {
        log.warn('client already unregistered or was never added, check your code');
        return; // error
      }
      clientsMap.delete(id);
    };

    const prepareChanges = () => {
      const allPromises: Promise<any>[] = [];
      clientsMap.forEach((client) => client.prepareFunc && allPromises.push(client.prepareFunc()));
      return Promise.all(allPromises);
    };

    const applyChanges = () => {
      const allPromises: Promise<any>[] = [];
      clientsMap.forEach((client) => client.applyFunc && allPromises.push(client.applyFunc()));
      return Promise.all(allPromises);
    };

    // set client invalid
    const invalid = (id: string) => {
      //console.log('client is invalid', id);
      if (!clientsMap.has(id)) {
        //log.warn('client not registered', id);
        return; // it happens when Formik is render but the useEffect for registering has not been called yet.
      }
      const client = clientsMap.get(id);
      client.valid = false;
      clientsMap.set(id, client);
    };

    // set client invalid
    const valid = (id: string) => {
      if (!clientsMap.has(id)) {
        //log.warn('client not registered', id);
        return; // error
      }
      const client = clientsMap.get(id);
      client.valid = true;
      clientsMap.set(id, client);
    };

    useImperativeHandle(ref, () => ({
      prepareChanges,
      applyChanges,
      invalid,
      valid,
      register,
      unregister,
      areAllClientsValid,
      debug,
    }));

    return <div className={className} {...rest}></div>;
  },
);

MultipleFormAggregator.displayName = 'MultipleFormAggregator';
